import React, { ReactNode } from 'react';
import { Link as ChakraLink } from '@chakra-ui/react';
import {
  Elements,
  RichText as PrismicRichText,
  RichTextProps,
} from 'prismic-reactjs';

import { Box, Heading, Link, Text } from '@/components/atoms';

import useLinkResolver from '@/hooks/useLinkResolver';

export type HTMLSerializer<T> = (
  type: Elements,
  element: any,
  content: string,
  children: T[],
  key: string,
  props: any
) => T | null;

export const htmlSerializer: HTMLSerializer<ReactNode> = (
  type,
  element,
  _content,
  children,
  key,
  { render, shouldHaveElements, ...props }
) => {
  const linkResolver = useLinkResolver();
  switch (type) {
    case Elements.list:
      return (
        <Box as="ul" listStyleType="circle" pl={10} {...props}>
          {children}
        </Box>
      );
    case Elements.listItem:
      return (
        <Text as="li" variant="md" mb={4} {...props}>
          {children}
        </Text>
      );
    case Elements.oList:
      return (
        <Box as="ol" pl={10} {...props}>
          {children}
        </Box>
      );
    case Elements.oListItem:
      return (
        <Text as="li" variant="md" mb={4} {...props}>
          {children}
        </Text>
      );
    case Elements.heading1:
      return (
        <Heading key={key} variant="h1" {...props}>
          {children}
        </Heading>
      );
    case Elements.heading2:
      return (
        <Heading key={key} variant="h2" {...props}>
          {children}
        </Heading>
      );
    case Elements.heading3:
      return (
        <Heading key={key} variant="h3" {...props}>
          {children}
        </Heading>
      );
    case Elements.heading4:
      return (
        <Heading key={key} variant="h4" {...props}>
          {children}
        </Heading>
      );
    case Elements.heading5:
      return (
        <Heading key={key} variant="h5" {...props}>
          {children}
        </Heading>
      );
    case Elements.heading6:
      return (
        <Heading key={key} variant="h6" {...props}>
          {children}
        </Heading>
      );
    case Elements.paragraph:
      if (shouldHaveElements) {
        return (
          <Text key={key} {...props}>
            {children}
          </Text>
        );
      }
      return children;
    case Elements.hyperlink:
      if (element.data.link_type === `Web`) {
        return (
          <ChakraLink
            href={element.data.url}
            target="_blank"
            rel="noopener noreferrer"
            textDecoration="underline"
          >
            {children}
          </ChakraLink>
        );
      }
    case Elements.hyperlink:
      if (element.data.link_type === `Document`) {
        return (
          <Link
            to={linkResolver(element.data)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </Link>
        );
      }

    default:
      return null;
  }
};

interface GenericProps {
  [index: string]: any;
}

export interface Props extends GenericProps, RichTextProps {
  shouldHaveElements?: boolean;
}

export const RichText = ({ shouldHaveElements, ...props }: Props) => {
  return (
    <PrismicRichText
      htmlSerializer={(type, element, content, children, key) =>
        htmlSerializer(type, element, content, children, key, {
          shouldHaveElements,
          ...props,
        })
      }
      render={props.render}
    />
  );
};
